/* eslint-disable react/jsx-no-bind */
import classNames from "classnames";
import { useEffect, useState } from "react";
import {
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "~/components/shared/DropdownMenu";
import Count from "~/components/build/Show/components/Count";
import { isSelected, toggleFilter } from "~/components/build/Show/components/Filter/lib";
import {
  FilterCategory,
  FilterOption,
  SelectedFilter,
} from "~/components/build/Show/components/Filter/types";

const checkboxItemClassName =
  "rounded-none px-3 py-1.5 text-sm hover:bg-gray-100 aria-checked:bg-purple-100";

const showHiddenItemClassName =
  "text-charcoal-400 hover:text-charcoal-600 w-full rounded-none px-3 py-1.5 text-left text-xs hover:bg-gray-100 data-[highlighted]:text-charcoal-600 data-[highlighted]:bg-gray-100";

function disableAutomaticClose(event: Event) {
  event.preventDefault();
}

type Props = {
  category: FilterCategory;
  selected: SelectedFilter[];
  onChange: (next: SelectedFilter[]) => void;
  /**
   * Test id prefix used for the "show hidden options" toggle. The toggle's
   * data-testid is `${testId}-${category.key}-show-hidden`.
   */
  testId: string;
};

function renderOption(
  category: FilterCategory,
  selected: SelectedFilter[],
  onChange: (next: SelectedFilter[]) => void,
  option: FilterOption,
  dimmed: boolean,
) {
  return (
    <DropdownMenuCheckboxItem
      key={option.value}
      id={`${category.key}-${option.value}`}
      className={classNames(checkboxItemClassName, dimmed && "opacity-75")}
      checked={isSelected(selected, category.key, option.value)}
      onCheckedChange={() => onChange(toggleFilter(selected, category.key, option.value))}
      onSelect={disableAutomaticClose}
    >
      <div className="grid min-w-0 flex-auto grid-cols-[1fr_auto] items-center gap-2 font-medium">
        <span className="flex items-center gap-1.5">
          {option.icon}
          {option.label}
        </span>
        {option.count != null && option.count > 0 && <Count>{option.count}</Count>}
      </div>
    </DropdownMenuCheckboxItem>
  );
}

/**
 * Renders the checkbox list of options for a single `FilterCategory`. Used
 * inside `FilterMenu` (under each category's group heading) and inside
 * `FilterChips` (per-chip mini-dropdown that lets users change the active
 * selection without going back to the main filter button).
 */
export default function CategoryOptions({ category, selected, onChange, testId }: Props) {
  const visibleOptions = category.options.filter((option) => !option.hidden);
  const hiddenOptions = category.options.filter((option) => option.hidden);

  const hasSelectedHidden = hiddenOptions.some((option) =>
    isSelected(selected, category.key, option.value),
  );

  const [showHidden, setShowHidden] = useState(hasSelectedHidden);

  // Auto-expand the hidden section if a previously hidden option becomes selected
  // while the menu is open.
  useEffect(() => {
    if (hasSelectedHidden) {
      setShowHidden(true);
    }
  }, [hasSelectedHidden]);

  const allHiddenAreEmpty = hiddenOptions.every((option) => (option.count ?? 0) === 0);
  const showHiddenLabel = allHiddenAreEmpty
    ? `${hiddenOptions.length} others with no results`
    : `${hiddenOptions.length} others`;

  return (
    <>
      {visibleOptions.map((option) => renderOption(category, selected, onChange, option, false))}

      {hiddenOptions.length > 0 && (
        <>
          <DropdownMenuSeparator className="mx-0 my-1 bg-gray-300" />
          {showHidden ? (
            hiddenOptions.map((option) => renderOption(category, selected, onChange, option, true))
          ) : (
            <DropdownMenuItem
              data-testid={`${testId}-${category.key}-show-hidden`}
              className={showHiddenItemClassName}
              onSelect={(event) => {
                event.preventDefault();
                setShowHidden(true);
              }}
            >
              {showHiddenLabel}
            </DropdownMenuItem>
          )}
        </>
      )}
    </>
  );
}
